import React, { useEffect, useMemo, useRef, useState } from 'react';

import cx from 'classnames';
import { debounce as lodashDebounce } from 'lodash';
import PropTypes from 'prop-types';
import CSSModule from 'react-css-modules';
import { X, ChevronRight, Edit2, Trash2, Plus } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';

import { actions as locationActions, duck as locationsDuck } from 'coupon-common/src/modules/locations';
import { getNormalizedEntity } from 'coupon-common/src/utils';

import styles from './BusinessInfoEditModal.module.scss';

const TABS = [
  { key: 'info', label: 'Business Info' },
  { key: 'media', label: 'Media' },
  { key: 'social', label: 'Social Links' },
];

// Same upload dropzone used on the sign-up "Add Your Branding" step
// (Authentication/MediaUpload.jsx) — reused here so logo/banner uploads
// look the same wherever they appear.
const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="1" y="1" width="18" height="18" rx="9" fill="#FBE4E1" />
    <path d="M10 5.5v7M7.2 8.3L10 5.5l2.8 2.8" stroke="#C0392B" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.5 13.2v.8a1.5 1.5 0 001.5 1.5h6a1.5 1.5 0 001.5-1.5v-.8" stroke="#C0392B" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

// Matches the platform set + brand icons used by the "Web & Social" picker
// on the MyBusinessAccount page, so both surfaces look and behave the same.
const MAX_SOCIAL_LINKS = 4;

// Same province → city lookup as LocationSearch.js (there is no free-text
// "search every city" endpoint — city search only exists scoped to a
// chosen province). Reusing the identical redux actions/selectors.
const provincesKey = locationsDuck.provincesEntity.key;
const citiesKey = locationsDuck.citiesEntity.key;

const InstagramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <rect x="2" y="2" width="14" height="14" rx="4" stroke="#E1306C" strokeWidth="1.4" />
    <circle cx="9" cy="9" r="3.4" stroke="#E1306C" strokeWidth="1.4" />
    <circle cx="12.8" cy="5.2" r="1" fill="#E1306C" />
  </svg>
);
const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="7" fill="#1877F2" />
    <path d="M11.1 9.3H9.7v5.4H7.6V9.3H6.5V7.5h1.1V6.3c0-1.3.6-2.4 2.5-2.4h1.5v1.8h-1c-.4 0-.6.2-.6.6v1.2h1.6l-.4 1.8Z" fill="#fff" />
  </svg>
);
const XSocialIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <rect x="1.5" y="1.5" width="15" height="15" rx="4" fill="#000" />
    <path d="M5 5l8 8M13 5l-8 8" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const WhatsAppIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="7" fill="#25D366" />
    <path d="M5.5 12.5 6 10.4a3.9 3.9 0 1 1 1.6 1.5l-2.1.6Z" fill="#fff" />
    <path
      d="M7.7 6.9c.15-.3.3-.3.45-.3h.35c.1 0 .25 0 .35.3.15.35.5 1.15.5 1.25.05.1.05.2 0 .3-.05.15-.1.2-.2.3l-.3.3c-.1.1-.2.2-.1.4.15.25.6.9 1.3 1.4.9.65 1.1.55 1.3.5.15-.05.4-.35.5-.5.1-.15.2-.15.35-.1l1.15.55c.15.05.25.1.3.2.05.15.05.55-.15.9-.2.4-.95.75-1.3.8-.35.05-.75.1-2.4-.9-1.55-.9-2.35-2.7-2.4-2.85-.05-.15-.5-.7-.5-1.35s.35-.95.5-1.1Z"
      fill="#25D366"
    />
  </svg>
);
const TelegramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="7" fill="#229ED9" />
    <path d="M4.5 8.9 13 5.6l-1.4 7.3c-.1.5-.4.6-.8.4l-2.2-1.6-1.1 1c-.1.1-.2.2-.4.2l.15-2.1 3.9-3.5c.17-.15-.04-.24-.26-.09L6 9.8 4 9.15c-.5-.15-.5-.5.1-.75Z" fill="#fff" />
  </svg>
);
const TikTokIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <rect x="1.5" y="1.5" width="15" height="15" rx="4" fill="#000" />
    <path
      d="M10.4 3.8h1.4c.1.9.7 1.7 1.6 2v1.5c-.6 0-1.2-.2-1.7-.5v3.5a3 3 0 1 1-3-3c.1 0 .2 0 .3.02v1.5a1.5 1.5 0 1 0 1.4 1.5V3.8Z"
      fill="#fff"
    />
  </svg>
);
const OtherLinkIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="7" stroke="#1C1C1C" strokeWidth="1.3" />
    <path d="M6.5 9.5a2 2 0 0 1 0-2.8l1-1a2 2 0 0 1 2.8 2.8" stroke="#1C1C1C" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M11.5 8.5a2 2 0 0 1 0 2.8l-1 1a2 2 0 0 1-2.8-2.8" stroke="#1C1C1C" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const SOCIAL_PLATFORMS = [
  { value: 'instagram', label: 'Instagram', Icon: InstagramIcon },
  { value: 'facebook', label: 'Facebook', Icon: FacebookIcon },
  { value: 'twitter', label: 'X / Twitter', Icon: XSocialIcon },
  { value: 'whatsapp', label: 'WhatsApp', Icon: WhatsAppIcon },
  { value: 'telegram', label: 'Telegram', Icon: TelegramIcon },
  { value: 'tiktok', label: 'TikTok', Icon: TikTokIcon },
  { value: 'other', label: 'Website / Other', Icon: OtherLinkIcon },
];

const getSocialPlatform = value =>
  SOCIAL_PLATFORMS.find(p => p.value === value) || SOCIAL_PLATFORMS[SOCIAL_PLATFORMS.length - 1];

const normalizeUrl = url => {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// Native <select> can't have its open option list styled, so this is a
// button + custom popover — mirrors the platform picker on MyBusinessAccount.
const SocialPlatformSelectBase = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = getSocialPlatform(value);

  useEffect(() => {
    const handleClickOutside = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div styleName="social-select" ref={wrapRef}>
      <button
        type="button"
        styleName={cx('social-select-btn', { open })}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <selected.Icon />
        <span styleName="social-select-label">{selected.label}</span>
        <span styleName={cx('social-select-chevron', { open })}>
          <ChevronRight size={14} />
        </span>
      </button>

      {open && (
        <div styleName="social-select-list" role="listbox">
          {SOCIAL_PLATFORMS.map(p => {
            const isSelected = p.value === value;
            return (
              <button
                key={p.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                styleName={cx('social-select-option', { selected: isSelected })}
                onClick={() => {
                  onChange(p.value);
                  setOpen(false);
                }}
              >
                <p.Icon />
                <span>{p.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

SocialPlatformSelectBase.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

SocialPlatformSelectBase.defaultProps = {
  value: 'instagram',
};

const SocialPlatformSelect = CSSModule(SocialPlatformSelectBase, styles, { allowMultiple: true });

const SocialLinkFormBase = ({ initialPlatform, initialTitle, initialUrl, onSave, onCancel }) => {
  const [platform, setPlatform] = useState(initialPlatform);
  const [customTitle, setCustomTitle] = useState(initialPlatform === 'other' ? initialTitle : '');
  const [url, setUrl] = useState(initialUrl);
  const [error, setError] = useState(null);

  const isOther = platform === 'other';

  const handleSave = () => {
    const title = isOther ? customTitle.trim() : getSocialPlatform(platform).label;
    if (!title || !url.trim()) {
      setError(isOther ? 'A name and a link are required.' : 'A link is required.');
      return;
    }
    onSave({ platform, title, url: normalizeUrl(url) });
  };

  return (
    <div styleName="social-form-card">
      <label styleName="field">
        <span styleName="field-label">Platform</span>
        <SocialPlatformSelect value={platform} onChange={setPlatform} />
      </label>

      {isOther && (
        <label styleName="field">
          <span styleName="field-label">Name</span>
          <input
            styleName="field-input"
            type="text"
            value={customTitle}
            onChange={e => setCustomTitle(e.target.value)}
            placeholder="e.g. Yelp"
          />
        </label>
      )}

      <label styleName="field">
        <span styleName="field-label">Link</span>
        <input
          styleName="field-input"
          type="url"
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="e.g. instagram.com/yourbusiness"
        />
      </label>

      {error && <p styleName="social-form-error">{error}</p>}

      <div styleName="social-form-actions">
        <button type="button" styleName="cancel-btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" styleName="save-btn" onClick={handleSave}>
          Save
        </button>
      </div>
    </div>
  );
};

SocialLinkFormBase.propTypes = {
  initialPlatform: PropTypes.string,
  initialTitle: PropTypes.string,
  initialUrl: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

SocialLinkFormBase.defaultProps = {
  initialPlatform: 'instagram',
  initialTitle: '',
  initialUrl: '',
};

const SocialLinkForm = CSSModule(SocialLinkFormBase, styles, { allowMultiple: true });

/**
 * Opened from the Business Info card's edit icon in MyCoupons — three
 * tabs: business info fields, logo/banner media, and social links.
 *
 * WIRING NOTE: `onSave` is called with the full form payload (text fields,
 * base64 image data for `logo`/`banner` — unchanged from `businessInfo` if
 * the user didn't pick a new file — plus a `socialLinks` array of
 * { platform, title, url }) when Save is clicked. This component does not
 * itself know your business-update Redux action; plug the actual dispatch
 * call into the `onSave` handler you pass in.
 */
const BusinessInfoEditModal = ({ isOpen, businessInfo, onClose, onSave, saving, error }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [form, setForm] = useState(() => buildInitialForm(businessInfo));
  const [logoPreview, setLogoPreview] = useState(businessInfo?.logo || null);
  const [bannerPreview, setBannerPreview] = useState(businessInfo?.cover_image || businessInfo?.banner || null);
  const [socialLinks, setSocialLinks] = useState(() => buildInitialSocialLinks(businessInfo));
  const [editingSocialIndex, setEditingSocialIndex] = useState(null); // number | 'new' | null
  const [logoDragging, setLogoDragging] = useState(false);
  const [bannerDragging, setBannerDragging] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const dispatch = useDispatch();
  const locations = useSelector(state => state.locations);

  // Reset form state whenever the modal is (re)opened, or the underlying
  // businessInfo changes (e.g. after a previous save round-trips through
  // redux) — avoids stale field values from a prior open.
  useEffect(() => {
    if (isOpen) {
      setForm(buildInitialForm(businessInfo));
      setLogoPreview(businessInfo?.logo || null);
      setBannerPreview(businessInfo?.cover_image || businessInfo?.banner || null);
      setSocialLinks(buildInitialSocialLinks(businessInfo));
      setEditingSocialIndex(null);
      setActiveTab('info');
      setAddressSuggestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Same lookups FormAddressAutoCompleteField.js uses (signup + the
  // account LocationFieldGroup) — fetched once the modal is open so
  // fetchPlaceDetails below has province/city entities to match against.
  useEffect(() => {
    if (isOpen) {
      dispatch(locationActions.getProvinces());
      dispatch(locationActions.getCities());
    }
  }, [isOpen, dispatch]);

  // Ported from FormAddressAutoCompleteField.js: same endpoints, same
  // debounce, same place-details → province/city match. That component
  // is built for react-final-form (form.getState()/form.change()); this
  // modal is plain useState, so the behavior is reproduced here against
  // updateField instead of dropping the component in directly.
  const fetchAddressSuggestions = async input => {
    if (!input) {
      setAddressSuggestions([]);
      return;
    }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/users/address-autocomplete/?query=${encodeURIComponent(input)}`,
      );
      const data = await res.json();
      setAddressSuggestions(data.predictions || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[BusinessInfoEditModal] Error fetching address suggestions:', err);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchAddressSuggestions = useMemo(() => lodashDebounce(fetchAddressSuggestions, 300), []);

  const fetchPlaceDetails = async placeId => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/users/place-details/?place_id=${placeId}`);
    const data = await response.json();

    if (data) {
      const provincesKey = locationsDuck.provincesEntity.key;
      const citiesKey = locationsDuck.citiesEntity.key;

      const { entityList: provinceOptions } = getNormalizedEntity(locations, provincesKey);
      const { entityList: cityOptions } = getNormalizedEntity(locations, citiesKey);

      const matchedProvince = provinceOptions.find(
        p => p.name.toLowerCase() === (data.province || '').toLowerCase(),
      );
      const matchedCity = cityOptions.find(c => c.name.toLowerCase() === (data.city || '').toLowerCase());

      const streetLine = [data.street_number, data.street].filter(Boolean).join(' ');
      if (streetLine) updateField('streetAddress', streetLine);

      if (matchedProvince) updateField('province', { id: matchedProvince.id, name: matchedProvince.name });
      if (matchedCity) updateField('city', { id: matchedCity.id, name: matchedCity.name });
      if (data.postal_code) updateField('postalCode', data.postal_code);
      updateField('country', data.country || 'Canada');
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[BusinessInfoEditModal] Error fetching place details:', err);
  }
};
  if (!isOpen) return null;

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const applyFile = (field, previewSetter, file) => {
    if (!file || !file.type.startsWith('image/')) return;
    updateField(field, file);
    const reader = new FileReader();
    reader.onload = () => previewSetter(reader.result);
    reader.readAsDataURL(file);
  };

  const handleFileChange = (field, previewSetter) => e => {
    applyFile(field, previewSetter, e.target.files && e.target.files[0]);
    e.target.value = ''; // allow re-selecting the same file next time
  };

  const makeDropHandlers = (field, previewSetter, setDragging) => ({
    onDragOver: e => {
      e.preventDefault();
      setDragging(true);
    },
    onDragLeave: () => setDragging(false),
    onDrop: e => {
      e.preventDefault();
      setDragging(false);
      applyFile(field, previewSetter, e.dataTransfer.files && e.dataTransfer.files[0]);
    },
  });

  const saveSocialLink = (index, value) => {
    setSocialLinks(prev => (index === 'new' ? [...prev, value] : prev.map((l, i) => (i === index ? value : l))));
    setEditingSocialIndex(null);
  };

  const removeSocialLink = index => {
    setSocialLinks(prev => prev.filter((_, i) => i !== index));
    if (editingSocialIndex === index) setEditingSocialIndex(null);
  };

const handleSave = () => {
  const payload = {
    ...form,
    // Unchanged: keep the original value so the diff in saga.js sees
    // no change. Changed: form.logo/form.banner already hold the real
    // File set by applyFile — use that instead of the base64 preview,
    // which formatToImageArgs can't turn into a Blob.
    logo: form.logo || businessInfo?.logo || null,
    banner: form.banner || businessInfo?.cover_image || businessInfo?.banner || null,
    socialLinks,
  };

    // eslint-disable-next-line no-console
    // console.log('[BusinessInfoEditModal] Save clicked — calling onSave with:', payload);

    if (onSave) {
      onSave(payload);
    } else {
      // eslint-disable-next-line no-console
      console.warn('[BusinessInfoEditModal] Save clicked but no onSave prop was passed in.');
    }
  };

  return (
    <div styleName="overlay" role="presentation" onClick={onClose}>
      <div
        styleName="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Edit business info"
        onClick={e => e.stopPropagation()}
      >
        <div styleName="header">
          <h2 styleName="header-title">Edit Business Info</h2>
          <button type="button" styleName="close-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div styleName="tabs">
          {TABS.map(tab => (
            <button
              key={tab.key}
              type="button"
              styleName={cx('tab-btn', { active: activeTab === tab.key })}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div styleName="body">
          {error && <p styleName="modal-error">{error}</p>}

          {activeTab === 'info' && (
            <div styleName="field-group">
              <label styleName="field">
                <span styleName="field-label">Business Name</span>
                <input
                  styleName="field-input"
                  type="text"
                  value={form.businessName}
                  onChange={e => updateField('businessName', e.target.value)}
                  placeholder="Your business name"
                />
              </label>

              <label styleName="field">
                <span styleName="field-label">Contact Name</span>
                <input
                  styleName="field-input"
                  type="text"
                  value={form.contactName}
                  onChange={e => updateField('contactName', e.target.value)}
                  placeholder="Primary contact"
                />
              </label>

              <div styleName="field-row">
                <label styleName="field">
                  <span styleName="field-label">Phone</span>
                  <input
                    styleName="field-input"
                    type="tel"
                    value={form.phone}
                    onChange={e => updateField('phone', e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </label>

                <label styleName="field">
                  <span styleName="field-label">Email Address</span>
                  <input
                    styleName="field-input"
                    type="email"
                    value={form.email}
                    disabled
                    placeholder="hello@business.com"
                  />
                </label>
              </div>

              <label styleName="field">
                <span styleName="field-label">Website</span>
                <input
                  styleName="field-input"
                  type="text"
                  value={form.website}
                  onChange={e => updateField('website', e.target.value)}
                  placeholder="www.business.com"
                />
              </label>

              <label styleName={cx('field', 'address-field')}>
                <span styleName="field-label">Street Address</span>
                <input
                  styleName="field-input"
                  type="text"
                  value={form.streetAddress}
                  onChange={e => {
                    updateField('streetAddress', e.target.value);
                    debouncedFetchAddressSuggestions(e.target.value);
                  }}
                  placeholder="e.g. 16 Dentith Rd"
                  autoComplete="off"
                />
                {addressSuggestions.length > 0 && (
                  <div styleName="address-suggestions">
                    {addressSuggestions.map((s, index) => (
                      <button
                        key={s.place_id || index}
                        type="button"
                        styleName="address-suggestion"
                        onClick={async () => {
                          updateField('streetAddress', s.description);
                          setAddressSuggestions([]);
                          await fetchPlaceDetails(s.place_id);
                        }}
                      >
                        {s.description}
                      </button>
                    ))}
                  </div>
                )}
              </label>

              {/* City/Province/Postal Code/Country are filled in from the
                  address pick above (fetchPlaceDetails) — same as
                  FormAddressAutoCompleteField.js in the signup flow. */}
              <div styleName="field-row">
                <label styleName="field">
                  <span styleName="field-label">City</span>
                  <input
                    styleName="field-input"
                    type="text"
                    value={form.city?.name || ''}
                    disabled
                    placeholder="Pick an address above"
                  />
                </label>

                <label styleName="field">
                  <span styleName="field-label">Province</span>
                  <input
                    styleName="field-input"
                    type="text"
                    value={form.province?.name || ''}
                    disabled
                    placeholder="Pick an address above"
                  />
                </label>
              </div>

              <div styleName="field-row">
                <label styleName="field">
                  <span styleName="field-label">Postal Code</span>
                  <input
                    styleName="field-input"
                    type="text"
                    value={form.postalCode}
                    disabled
                    placeholder="Pick an address above"
                  />
                </label>

                <label styleName="field">
                  <span styleName="field-label">Country</span>
                  <input
                    styleName="field-input"
                    type="text"
                    value={form.country}
                    disabled
                    placeholder="Pick an address above"
                  />
                </label>
              </div>

              <label styleName="field">
                <span styleName="field-label">Terms & Conditions</span>
                <textarea
                  styleName="field-input"
                  rows={3}
                  value={form.termsAndConditions}
                  onChange={e => updateField('termsAndConditions', e.target.value)}
                  placeholder="e.g. Coupon cannot be combined with any other discount or offer."
                />
              </label>
            </div>
          )}

          {activeTab === 'media' && (
            <div styleName="field-group">
              <div styleName="media-field">
                <span styleName="field-label">Business Logo</span>
                <div
                  styleName={cx('upload-box', { dragging: logoDragging })}
                  {...makeDropHandlers('logo', setLogoPreview, setLogoDragging)}
                >
                  {logoPreview && (
                    <img src={logoPreview} alt="Business logo preview" styleName="upload-preview-circle" />
                  )}
                  <div styleName="upload-drop" onClick={() => logoInputRef.current && logoInputRef.current.click()}>
                    <UploadIcon />
                    <p styleName="upload-drop-text">
                      <a
                        href="#"
                        styleName="upload-drop-link"
                        onClick={e => {
                          e.preventDefault();
                          logoInputRef.current && logoInputRef.current.click();
                        }}
                      >
                        Click here
                      </a>{' '}
                      to upload your file or drag.
                    </p>
                    <span styleName="upload-drop-caption">Supported Format: SVG, JPG, PNG (10mb each)</span>
                  </div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange('logo', setLogoPreview)}
                  />
                </div>
              </div>

              <div styleName="media-field">
                <span styleName="field-label">Banner Image</span>
                <div
                  styleName={cx('upload-box', { dragging: bannerDragging })}
                  {...makeDropHandlers('banner', setBannerPreview, setBannerDragging)}
                >
                  {bannerPreview && (
                    <img src={bannerPreview} alt="Banner preview" styleName="upload-preview-banner" />
                  )}
                  <div styleName="upload-drop" onClick={() => bannerInputRef.current && bannerInputRef.current.click()}>
                    <UploadIcon />
                    <p styleName="upload-drop-text">
                      <a
                        href="#"
                        styleName="upload-drop-link"
                        onClick={e => {
                          e.preventDefault();
                          bannerInputRef.current && bannerInputRef.current.click();
                        }}
                      >
                        Click here
                      </a>{' '}
                      to upload your file or drag.
                    </p>
                    <span styleName="upload-drop-caption">Supported Format: SVG, JPG, PNG (10mb each)</span>
                  </div>
                  <input
                    ref={bannerInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange('banner', setBannerPreview)}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'social' && (
            <div styleName="field-group">
              {socialLinks.length === 0 && editingSocialIndex === null && (
                <p styleName="social-empty">No social links added yet.</p>
              )}

              {socialLinks.map((link, i) =>
                editingSocialIndex === i ? (
                  <SocialLinkForm
                    key={i}
                    initialPlatform={link.platform}
                    initialTitle={link.title}
                    initialUrl={link.url}
                    onSave={value => saveSocialLink(i, value)}
                    onCancel={() => setEditingSocialIndex(null)}
                  />
                ) : (
                  <div key={i} styleName="social-link-row">
                    <div styleName="social-link-main">
                      <span styleName="social-link-icon">
                        {React.createElement(getSocialPlatform(link.platform).Icon)}
                      </span>
                      <div styleName="social-link-text">
                        <span styleName="social-link-title">{link.title}</span>
                        <span styleName="social-link-url">{link.url}</span>
                      </div>
                    </div>
                    <div styleName="social-link-actions">
                      <button
                        type="button"
                        styleName="icon-btn"
                        onClick={() => setEditingSocialIndex(i)}
                        aria-label={`Edit ${link.title}`}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        type="button"
                        styleName="icon-btn-danger"
                        onClick={() => removeSocialLink(i)}
                        aria-label={`Delete ${link.title}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ),
              )}

              {editingSocialIndex === 'new' && (
                <SocialLinkForm onSave={value => saveSocialLink('new', value)} onCancel={() => setEditingSocialIndex(null)} />
              )}

              {editingSocialIndex === null && socialLinks.length < MAX_SOCIAL_LINKS && (
                <button type="button" styleName="add-link-btn" onClick={() => setEditingSocialIndex('new')}>
                  <Plus size={14} />
                  Add link
                </button>
              )}
            </div>
          )}
        </div>

        <div styleName="footer">
          <button type="button" styleName="cancel-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" styleName="save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// city/province come back from the API as lookup objects ({ id, name,
// abbreviation }), not plain strings — this pulls a displayable/editable
// label out of either shape so it's safe to put in a text input.
function getLocationLabel(value) {
  if (!value) return '';
  if (typeof value === 'object') return value.name || value.abbreviation || '';
  return String(value);
}

function buildInitialForm(businessInfo) {
  const cityValue = businessInfo?.city;
  const provinceValue = businessInfo?.province;
  return {
    businessName: businessInfo?.business_name || businessInfo?.name || '',
    contactName: businessInfo?.contact_name || businessInfo?.first_name || '',
    phone: businessInfo?.phone || '',
    email: businessInfo?.email || '',
    website: businessInfo?.website || '',
    streetAddress: businessInfo?.street || businessInfo?.address || '',
    // { id, name } — not a plain string. The update endpoint's `city`
    // field is a PrimaryKeyRelatedField and needs a real city id, which
    // free text can't supply. Set via the address autocomplete below
    // (fetchPlaceDetails), same as province.
    city: cityValue && typeof cityValue === 'object'
      ? { id: cityValue.id, name: getLocationLabel(cityValue) }
      : null,
    // province is read-only server-side anyway (api.js explicitly skips
    // it on save) — kept as { id, name } purely for display, matched the
    // same way city is.
    province: provinceValue && typeof provinceValue === 'object' && provinceValue.id
      ? { id: provinceValue.id, name: getLocationLabel(provinceValue) }
      : null,
    postalCode: businessInfo?.postal_code || '',
    country: businessInfo?.country || '',
    termsAndConditions: businessInfo?.terms_and_conditions || '',
    logo: null, // File, only set if the user picks a new one
    banner: null, // File, only set if the user picks a new one
  };
}

// Existing single-URL fields (if present) seed the initial links list; new
// links added in the modal live only as { platform, title, url } entries,
// matching the MyBusinessAccount "Web & Social" link shape.
function buildInitialSocialLinks(businessInfo) {
  if (Array.isArray(businessInfo?.social_links)) return businessInfo.social_links;

  const links = [];
  if (businessInfo?.facebook_url) {
    links.push({ platform: 'facebook', title: getSocialPlatform('facebook').label, url: businessInfo.facebook_url });
  }
  if (businessInfo?.instagram_url) {
    links.push({ platform: 'instagram', title: getSocialPlatform('instagram').label, url: businessInfo.instagram_url });
  }
  if (businessInfo?.twitter_url) {
    links.push({ platform: 'twitter', title: getSocialPlatform('twitter').label, url: businessInfo.twitter_url });
  }
  if (businessInfo?.website_url) {
    links.push({ platform: 'other', title: 'Website', url: businessInfo.website_url });
  }
  return links;
}

BusinessInfoEditModal.propTypes = {
  isOpen: PropTypes.bool,
  businessInfo: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  saving: PropTypes.bool,
  error: PropTypes.string,
};

BusinessInfoEditModal.defaultProps = {
  isOpen: false,
  businessInfo: {},
  onClose: undefined,
  onSave: undefined,
  saving: false,
  error: null,
};

export default CSSModule(BusinessInfoEditModal, styles, { allowMultiple: true });